import React from 'react';
import { Feather } from '@expo/vector-icons';
import { View, Image, TouchableOpacity } from 'react-native';
import { connect } from 'react-redux';

import { app as appActions } from '../../actions';
import { getProductImageUrl } from '../../../common/utils/products/productImageUrlResolver';
import { InputNumberSuffixed, InputParlevelQuantity } from '../../components/input/index';
import { MEASUREMENT_UNIT_SHORT_NAME } from '../../../common/constants/index';
import { useUserContext } from '../../../common/utils/context';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { ProductSelector } from '../../components/selector';
import { SwitchWithLabel, Text } from '../../components';
import { PrimaryButton } from '../../design/components';
import { useTheme } from '../../theme';
import { useForm } from '../../../common/utils/hooks';
import { valueExists } from '../../../common/utils/utils';
import useDialogSaveButton from '../../hooks/useDialogSaveButton';

const ConfirmCoffeeMachineValuesItem = ({
	item,
	setItemValueCreator,
	setItemValuesCreator,
	value,
	openDialog,
}) => {
	const { tenantSettings } = useUserContext();
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();

	const replacePlanogramProduct = value?.replacePlanogramProduct || false;
	const product = value?.product ? value.product : item.product;
	const productSize = value?.product ? value.productSize : item.product?.size;
	const productSizeUnit = value?.product ? value.productSizeUnit : item.product?.sizeUnit;
	const productMeasurementUnit = value?.product ? value.productMeasurementUnit : item.product?.measurementUnit;

	const quantityProductSizeUnit = valueExists(value?.quantityProductSizeUnit) ? value.quantityProductSizeUnit : item.quantityProductSizeUnit;
	const quantity = valueExists(value?.quantity) ? value.quantity : item.quantity;

	const { selection } = item;

	return (
		<View style={styles.pickItemWrapper}>
			<TouchableOpacity
				onPress={() => {
					openDialog(() => {
						// eslint-disable-next-line react-hooks/rules-of-hooks
						const { changeFormValue, values } = useForm({
							replacePlanogramProduct,
							product,
							productSize,
							productSizeUnit,
							productMeasurementUnit,
						});

						const modifyFunction = setItemValuesCreator(item.id);

						// eslint-disable-next-line react-hooks/rules-of-hooks
						const { isSaveButtonPressed, onSaveButtonPress } = useDialogSaveButton(() => {
							return modifyFunction(values);
						});

						return (
							<View style={styles.dialog}>
								<Text style={styles.dialogText}>Product</Text>
								<ProductSelector
									initialFilter={{
										isIngredient: true,
									}}
									initialValue={values.product}
									onChange={newProduct => {
										changeFormValue({
											product: newProduct,
											productSize: newProduct.size,
											productSizeUnit: newProduct.sizeUnit,
											productMeasurementUnit: newProduct.measurementUnit,
										});
									}}
								/>
								<SwitchWithLabel
									onValueChange={newValue => {
										changeFormValue({ replacePlanogramProduct: newValue });
									}}
									field="replacePlanogramProduct"
									value={values.replacePlanogramProduct}
									label="Replace product permanently"
								/>
								<PrimaryButton
									size="compact"
									label="OK"
									loading={isSaveButtonPressed}
									onPress={onSaveButtonPress}
								/>
							</View>
						);
					});
				}}
				style={[
					styles.pickItem,
				]}
			>
				<View style={styles.selectionColumn}>
					{Array.isArray(selection)
						? selection.map((item, i) => {
							return (
								<Text
									key={i}
									style={styles.selection}
								>
									{item}
								</Text>
							);
						})
						: <Text style={styles.selection}>{selection.substring(0, 3)}</Text>
					}
				</View>

				<View style={styles.imageColumn}>
					{
						Boolean(product) && (
							<Image
								style={styles.productImg}
								source={{ uri: getProductImageUrl(product) }}
							/>
						)
					}
				</View>

				<View style={styles.mainInformation}>
					<Text style={styles.productName}>
						{ product ? product.name : 'No product' }
					</Text>
				</View>
			</TouchableOpacity>
			<View style={styles.inputColumn}>
				{
					Boolean(product) && (
						<View style={styles.setInventoryContainer}>
							{
								tenantSettings.useProductSizeUnit ? (
									<>
										<InputNumberSuffixed
											onChange={setItemValueCreator(item.id, 'quantityProductSizeUnit')}
											value={quantityProductSizeUnit}
											textAlign="left"
											multiplyByHundred={2}
											decimalPlaces={2}
											roundToSignificantFigures={true}
											suffix={productSize ? `x ${productSize} ${MEASUREMENT_UNIT_SHORT_NAME[productSizeUnit]}` : ''}
											disabled={!product}
											style={{ width: 40, height: 44 }}
											wrapperStyle={{ width: 110 }}
										/>
										<View style={styles.stepButtons}>
											<TouchableOpacity
												onPress={() => setItemValueCreator(item.id, 'quantityProductSizeUnit')((quantityProductSizeUnit || 0) + 100)}
												style={styles.incrementAndDecrement}
												accessibilityRole="button"
												accessibilityLabel="Add one"
											>
												<Feather name="plus" size={18} color={colors.accent} />
											</TouchableOpacity>
											<TouchableOpacity
												onPress={() => setItemValueCreator(item.id, 'quantityProductSizeUnit')(Math.max(0, (quantityProductSizeUnit || 0) - 100))}
												style={styles.incrementAndDecrement}
												accessibilityRole="button"
												accessibilityLabel="Remove one"
											>
												<Feather name="minus" size={18} color={colors.accent} />
											</TouchableOpacity>
										</View>
									</>
								) : (
									<InputParlevelQuantity
										onChange={setItemValueCreator(item.id, 'quantity')}
										value={quantity}
										product={product}
										suffix={product && MEASUREMENT_UNIT_SHORT_NAME[productMeasurementUnit]}
										disabled={!product}
									/>
								)
							}
						</View>
					)
				}
			</View>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	pickItemWrapper: {
		backgroundColor: colors.canvas,
		borderBottomColor: colors.border,
		borderBottomWidth: 1,
		paddingVertical: space.md,
		paddingHorizontal: space.gutter,
		width: '100%',
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.sm,
		minHeight: 68,
	},
	pickItem: {
		flex: 1,
		flexDirection: 'row',
		alignItems: 'center',
		gap: 10,
	},
	selectionColumn: {
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 32,
	},
	selection: {
		...type.cardTitle,
		color: colors.text,
	},
	imageColumn: {
		alignItems: 'center',
		justifyContent: 'center',
	},
	productImg: {
		height: 48,
		width: 48,
		resizeMode: 'contain', // Fits image into container with correct aspect ratio
	},
	mainInformation: {
		flexShrink: 1,
	},
	productName: {
		...type.rowTitle,
		color: colors.text,
	},
	inputColumn: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	setInventoryContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
	},
	stepButtons: {
		flexDirection: 'row',
		gap: space.xs,
		marginLeft: space.xs,
	},
	incrementAndDecrement: {
		borderWidth: 1,
		borderColor: colors.borderStrong,
		borderRadius: radius.pill,
		width: 44,
		height: 44,
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: colors.card,
	},
	dialog: {
		paddingTop: space.lg,
		gap: space.md,
	},
	dialogText: {
		...type.caption,
		color: colors.textSecondary,
		marginBottom: -space.sm,
	},
});

const mapDispatchToProps = dispatch => ({
	openDialog: component => dispatch(appActions.openDialog(component)),
});

export default connect(null, mapDispatchToProps)(ConfirmCoffeeMachineValuesItem);
